import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCloudUploadAlt } from 'react-icons/fa';
import './Upload.css';

const Upload = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    validateAndSetFiles(files);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    validateAndSetFiles(files);
  };

  // Validate files (type, size, etc.)
  const validateAndSetFiles = (files) => {
    setError('');
    
    // Example validation - can be expanded based on requirements
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    const oversizedFiles = files.filter(file => file.size > maxFileSize);
    
    if (invalidFiles.length > 0) {
      setError('يرجى تحميل ملفات بتنسيق PDF أو Word أو نص فقط.');
      return;
    }
    
    if (oversizedFiles.length > 0) {
      setError('حجم الملف يجب أن لا يتجاوز 10 ميجابايت.');
      return;
    }
    
    setSelectedFiles([...selectedFiles, ...files]);
  };
  
  // Remove a file from selection
  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  // Trigger file input click
  const browseFiles = () => {
    fileInputRef.current.click();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('يرجى تحميل ملف واحد على الأقل.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Placeholder for API integration
      // Here you would typically upload the files to your backend
      // const formData = new FormData();
      // selectedFiles.forEach(file => {
      //   formData.append('files', file);
      // });
      
      // const response = await fetch('YOUR_API_ENDPOINT', {
      //   method: 'POST',
      //   body: formData
      // });
      
      // if (response.ok) {
      //   // Handle successful upload
      // }
      
      // For now, we'll simulate a successful upload after a delay
      setTimeout(() => {
        setLoading(false);
        // Navigate to payment page after upload
        navigate('/payment1');
      }, 2000);
    } catch (error) {
      setLoading(false);
      setError('حدث خطأ أثناء تحميل الملفات. يرجى المحاولة مرة أخرى.');
      console.error('Upload error:', error);
    }
  };

  return (
    <div className="upload-page">
      <div className="container">
        <div className="upload-container">
          <h1>{t('upload.title')}</h1>
          <p className="upload-subtitle">{t('upload.subtitle')}</p>
          
          {error && <div className="error-message">{error}</div>}
          
          <div 
            className={`upload-area ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FaCloudUploadAlt className="upload-icon" />
            <p>{t('upload.drag')}</p>
            <p className="upload-or">{t('upload.or')}</p>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={browseFiles}
            >
              {t('upload.browse')}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: 'none' }}
            />
          </div>
          
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h3>الملفات المحددة</h3>
              <ul>
                {selectedFiles.map((file, index) => (
                  <li key={index}>
                    <span>{file.name}</span>
                    <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <button 
                      type="button" 
                      className="remove-file" 
                      onClick={() => removeFile(index)}
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="upload-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate('/')}
            >
              {t('upload.back')}
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || selectedFiles.length === 0}
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                t('upload.next')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload; 